import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar,
  Home,
  Clock,
  User,
  MapPin,
  Phone,
  Loader2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Play
} from 'lucide-react';

const AdminMissions = () => {
  const [appointments, setAppointments] = useState([]);
  const [homeCare, setHomeCare] = useState([]);
  const [appointmentStats, setAppointmentStats] = useState({});
  const [homeCareStats, setHomeCareStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('appointments');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [appointmentsRes, homeCareRes] = await Promise.all([
        adminAPI.getAllAppointments(),
        adminAPI.getAllHomeCare()
      ]);
      
      setAppointments(appointmentsRes.data.appointments || []);
      setAppointmentStats(appointmentsRes.data.stats || {});
      setHomeCare(homeCareRes.data.requests || []);
      setHomeCareStats(homeCareRes.data.stats || {});
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
      confirmed: { label: 'Confirmé', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      accepted: { label: 'Accepté', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
      in_progress: { label: 'En cours', color: 'bg-cyan-100 text-cyan-700', icon: Play },
      completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      cancelled: { label: 'Annulé', color: 'bg-slate-100 text-slate-700', icon: XCircle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const getTypeLabel = (type) => {
    const types = {
      consultation: 'Consultation',
      video_call: 'Téléconsultation',
      home_visit: 'Visite à domicile',
    };
    return types[type] || type;
  };

  const getCareTypeLabel = (type) => {
    const types = {
      nursing: 'Soins infirmiers',
      physiotherapy: 'Kinésithérapie',
      elderly_care: 'Soins personnes âgées',
      pediatric: 'Soins pédiatriques',
      post_op: 'Soins post-opératoires',
      other: 'Autre'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 
          className="text-2xl font-bold text-[#1A237E]"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Missions & Rendez-vous
        </h1>
        <p className="text-slate-600 mt-1">Vue d'ensemble de toutes les activités</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#1A237E]">{appointmentStats.total || 0}</p>
            <p className="text-sm text-slate-500">Total RDV</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-amber-500">{appointmentStats.pending || 0}</p>
            <p className="text-sm text-slate-500">RDV en attente</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#1A237E]">{homeCareStats.total || 0}</p>
            <p className="text-sm text-slate-500">Total Missions</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-emerald-500">{homeCareStats.completed || 0}</p>
            <p className="text-sm text-slate-500">Missions terminées</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger 
            value="appointments" 
            className="rounded-lg data-[state=active]:bg-white"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Rendez-vous ({appointments.length})
          </TabsTrigger>
          <TabsTrigger 
            value="homecare"
            className="rounded-lg data-[state=active]:bg-white"
          >
            <Home className="w-4 h-4 mr-2" />
            Soins à domicile ({homeCare.length})
          </TabsTrigger>
        </TabsList>

        {/* Appointments Tab */}
        <TabsContent value="appointments" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Patient</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Professionnel</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Type</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Date</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Statut</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Paiement</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {appointments.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          Aucun rendez-vous
                        </td>
                      </tr>
                    ) : (
                      appointments.map((apt) => (
                        <tr key={apt.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-slate-900">{apt.patient_name}</span>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-slate-700">Dr. {apt.professional_name}</span>
                            <p className="text-xs text-slate-500">{apt.professional_specialty}</p>
                          </td>
                          <td className="p-4">
                            <span className="text-slate-600">{getTypeLabel(apt.appointment_type)}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-slate-700">{apt.date}</span>
                            <p className="text-xs text-slate-500">{apt.time_slot}</p>
                          </td>
                          <td className="p-4">
                            {getStatusBadge(apt.status)}
                          </td>
                          <td className="p-4">
                            <Badge className={apt.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                              {apt.payment_status === 'paid' ? 'Payé' : 'Non payé'}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Home Care Tab */}
        <TabsContent value="homecare" className="mt-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Patient</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Type de soin</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Adresse</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Professionnel</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">Statut</th>
                      <th className="text-left p-4 text-sm font-medium text-slate-600">RDV</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {homeCare.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-slate-500">
                          Aucune mission
                        </td>
                      </tr>
                    ) : (
                      homeCare.map((req) => (
                        <tr key={req.id} className="hover:bg-slate-50">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <div>
                                <span className="font-medium text-slate-900">{req.patient_name}</span>
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {req.phone}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-slate-700">{getCareTypeLabel(req.care_type)}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-slate-600 flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {req.address?.substring(0, 30)}...
                            </span>
                          </td>
                          <td className="p-4">
                            {req.professional_name ? (
                              <span className="text-slate-700">{req.professional_name}</span>
                            ) : (
                              <span className="text-slate-400 italic">Non assigné</span>
                            )}
                          </td>
                          <td className="p-4">
                            {getStatusBadge(req.status)}
                          </td>
                          <td className="p-4">
                            {req.confirmed_date ? (
                              <span className="text-slate-700">
                                {req.confirmed_date}
                                <p className="text-xs text-slate-500">{req.confirmed_time}</p>
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminMissions;
